import { useUrlSearchParams } from "@frend-digital/ui/next";
import type React from "react";
import { Suspense } from "react";
export type WithSearchParamsProps = {};

export function withSearchParams<
  T extends WithSearchParamsProps = WithSearchParamsProps,
>(
  WrappedComponent: React.ComponentType<
    T & {
      searchParams: URLSearchParams;
    }
  >,
) {
  // Try to create a nice displayName for React Dev Tools.
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || "Component";

  const ComponentWithSearchParams = (
    props: Omit<T, keyof WithSearchParamsProps>,
  ) => {
    // Fetch the props you want to inject. This could be done with context instead.
    const searchParams = useUrlSearchParams();

    // props comes afterwards so the can override the default ones.
    return <WrappedComponent {...(props as T)} searchParams={searchParams} />;
  };

  // Creating the inner component. The calculated Props type here is the where the magic happens.
  const ComponentWithTheme = (props: Omit<T, keyof WithSearchParamsProps>) => {
    // Fetch the props you want to inject. This could be done with context instead.

    // props comes afterwards so the can override the default ones.
    return (
      <Suspense
        fallback={
          <WrappedComponent
            {...(props as T)}
            searchParams={new URLSearchParams()}
          />
        }
      >
        <ComponentWithSearchParams {...(props as T)} />
      </Suspense>
    );
  };

  ComponentWithTheme.displayName = `withSearchParams(${displayName})`;

  return ComponentWithTheme;
}
